import { useQuery } from '@tanstack/react-query';
import { checkInstallDir, type CreatePairingRequest, type CreatePairingResponse, type PairingOs } from '@couch/shared';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { CopyField } from '../../components/CopyField';
import { CloudChoices } from './CloudBadge';
import { FormError, useSubmit } from '../../components/Field';
import { useDialog } from '../../components/useDialog';

/** Where the installer puts ComfyUI and Couch (doc 49): a typed folder, the machine's own picker, or the default. */
type Where = { dir: string } | { pick: true } | null;

const DEFAULT_DIR: Readonly<Record<PairingOs, string>> = Object.freeze({ macos: '~/Couch', windows: 'C:\\Users\\<you>\\Couch', linux: '~/Couch' });
const EXAMPLE_DIR: Readonly<Record<PairingOs, string>> = Object.freeze({ macos: '/Volumes/T7/Couch', windows: 'D:\\Couch', linux: '/data/Couch' });
const SYSTEMS: readonly [PairingOs, string][] = [['macos', 'macOS (Apple Silicon)'], ['windows', 'Windows'], ['linux', 'Linux (NVIDIA or CPU) — also a cloud VM']];

function pasteInto(os: PairingOs, where: Where): string {
  const mac = os === 'macos';
  const into = where === null ? (os === 'windows' ? 'your user folder' : '~/Couch')
    : 'dir' in where ? where.dir : `the folder you choose in the ${mac ? 'Finder window' : 'folder window'} that opens`;
  if (os === 'linux') {
    return `Paste this into a terminal on the Linux machine (over SSH for a cloud VM), as your normal user — not root — and press Enter. It installs ComfyUI and the Couch agent into ${into} (about 3–6 GB) and connects the machine.`;
  }
  return mac
    ? `Paste this into Terminal on the Mac and press Return. It installs ComfyUI and the Couch agent into ${into} (about 4 GB) and connects the Mac.`
    : `Paste this into PowerShell on the PC (not as administrator) and press Enter. It installs ComfyUI and the Couch agent into ${into} (about 5 GB) and connects the PC.`;
}

function InstallFolder({ os, dir, pick, problem, onDir, onPick }: {
  os: PairingOs; dir: string; pick: boolean; problem: string | undefined; onDir: (dir: string) => void; onPick: (pick: boolean) => void;
}) {
  return (
    <div className="field install-folder">
      <label htmlFor="install-dir">Install folder (optional)</label>
      <input id="install-dir" value={pick ? '' : dir} placeholder={DEFAULT_DIR[os]} disabled={pick} spellCheck={false} autoComplete="off"
        aria-describedby="install-dir-hint" aria-invalid={problem ? 'true' : undefined} onChange={(e) => { onDir(e.target.value); }} />
      {problem && <p role="alert" className="form-error">{problem}</p>}
      <p id="install-dir-hint" className="muted">
        Empty = the default. Another drive (e.g. {EXAMPLE_DIR[os]}) keeps models off a small system disk;
        it must be connected whenever the machine should work.
      </p>
      {os !== 'linux' && (
        <label className="checkbox">
          <input type="checkbox" checked={pick} onChange={(e) => { onPick(e.target.checked); }} />
          Pick the folder on the machine — a folder window opens there during the install
        </label>
      )}
    </div>
  );
}

function ChooseSystem({ onCreated }: { onCreated: (created: CreatePairingResponse, where: Where) => void }) {
  const [os, setOs] = useState<PairingOs>(navigator.userAgent.includes('Windows') ? 'windows' : 'macos');
  const [dir, setDir] = useState('');
  const [pick, setPick] = useState(false);
  const [problem, setProblem] = useState<string>();
  // Linux servers have no screen, so no folder picker there (doc 50).
  const picking = pick && os !== 'linux';
  const where = (): Where => (picking ? { pick: true } : dir.trim() ? { dir: dir.trim() } : null);
  const form = useSubmit(async () => {
    const chosen = where();
    const body: CreatePairingRequest = chosen === null ? { os } : 'dir' in chosen ? { os, installDir: chosen.dir } : { os, pickFolder: true };
    if (chosen && 'dir' in chosen) {
      const check = checkInstallDir(os, chosen.dir);
      setProblem('problem' in check ? check.problem : undefined);
      if ('problem' in check) throw new Error('Check the install folder.');
    }
    return { created: await api.createPairing(body), where: chosen };
  }, (result) => { onCreated(result.created, result.where); });
  return (
    <form onSubmit={form.submit}>
      <fieldset className="os-choice">
        <legend>Which system does the machine run?</legend>
        {SYSTEMS.map(([value, label]) => (
          <label key={value}>
            <input type="radio" name="os" value={value} checked={os === value} onChange={() => { setOs(value); setProblem(undefined); }} />
            {label}
          </label>
        ))}
      </fieldset>
      {os === 'linux' && <CloudChoices />}
      <InstallFolder os={os} dir={dir} pick={picking} problem={problem ?? form.fieldErrors.installDir}
        onDir={(value) => { setDir(value); setProblem(undefined); }} onPick={setPick} />
      <FormError message={form.formError} />
      <button type="submit" disabled={form.pending}>Show the command</button>
    </form>
  );
}

function WaitForMachine({ created, where, onPaired, onRestart }: { created: CreatePairingResponse; where: Where; onPaired: () => void; onRestart: () => void }) {
  const status = useQuery({
    queryKey: ['pairing', created.pairing.id],
    queryFn: () => api.getPairing(created.pairing.id),
    refetchInterval: 2000,
  });
  const state = status.data?.pairing.status ?? 'pending';
  useEffect(() => { if (state === 'paired') onPaired(); }, [state, onPaired]);
  const until = new Date(created.pairing.expiresAt).toLocaleTimeString(undefined, { timeStyle: 'short' });
  return (
    <div className="pairing">
      <p>{pasteInto(created.pairing.os, where)}</p>
      <CopyField value={created.command} label="Command" />
      {state === 'expired' ? (
        <p role="alert" className="form-error">This command expired. <button type="button" className="button-quiet" onClick={onRestart}>New command</button></p>
      ) : (
        <p role="status" className="muted">Waiting for the machine to connect… The command works once, until {until}.</p>
      )}
      {status.isError && <p role="alert" className="form-error">{errorMessage(status.error)}</p>}
    </div>
  );
}

/** A modal that hands out the one-liner and closes once the machine has paired. Each opening starts with the default folder. */
export function AddMachineDialog({ onClose, onPaired }: { onClose: () => void; onPaired: () => void }) {
  const [created, setCreated] = useState<{ response: CreatePairingResponse; where: Where } | null>(null);
  const ref = useDialog(onClose);
  return (
    <div className="overlay">
      <div ref={ref} className="dialog" role="dialog" aria-modal="true" aria-labelledby="add-machine-title">
        <h2 id="add-machine-title">Add a machine</h2>
        {created
          ? <WaitForMachine created={created.response} where={created.where} onPaired={onPaired} onRestart={() => { setCreated(null); }} />
          : <ChooseSystem onCreated={(response, where) => { setCreated({ response, where }); }} />}
        <button type="button" className="button-quiet dialog-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}
